/*
 * Purpose: Render OTP input field, channel selector, request/resend button, and validation message.
 * Boundary: Layout only; no internal state or side effects; controlled externally.
 * Invariants:
 * - Displays success or error message beneath input.
 * - Button text reflects request state and cooldown.
 * - Channel selector renders when 2+ channels available and no external override.
 * - Channel selector locks during cooldown.
 * - ASCII only; all comments and strings are plain text.
 */

// Imports
import { OtpInput, RequestButton, ValidateButton } from './otp-controls';

// Channel display labels
const CHANNEL_LABELS = { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email' };

// Component OtpLayout:
// Stateless layout composing OTP input, channel selector, and request button
// Receives state and handlers from useOtp
export default function OtpLayout({
    otpValue,
    onOtpChange,
    validateOtp,
    isValid,
    error,
    cooldown,
    hasRequested,
    requestOtp,
    resendOtp,
    isProceedReadyOtp,
    otpLength,
    selectedChannel,
    availableChannels,
    showChannelSelector,
    setSelectedChannel,
}) {
    // Rendering - layout with channel selector, labeled input, action button, and feedback message
    return (
        <div>
            {/* Heading - contextually set by mode */}
            <h1 id="otp-title" className="text-sm md:text-sm text-neutral-300 mb-1">
                One-Time Pin (OTP)
            </h1>

            {/* Channel Selector - only when 2+ channels available and no external override */}
            {showChannelSelector && (
                <div className={`flex items-center gap-2 mb-2 ${cooldown > 0 ? 'opacity-50 pointer-events-none' : ''}`}>
                    <span className="text-xs text-neutral-400">
                        {hasRequested ? 'Sent via:' : 'Send via:'}
                    </span>
                    <div className="flex gap-1.5">
                        {availableChannels.map(ch => (
                            <button
                                key={ch}
                                type="button"
                                onClick={() => setSelectedChannel(ch)}
                                className={
                                    ch === selectedChannel
                                        ? 'px-2.5 py-0.5 rounded text-xs font-medium bg-emerald-600 text-white transition'
                                        : 'px-2.5 py-0.5 rounded text-xs font-medium bg-neutral-800 text-neutral-400 hover:text-neutral-300 transition'
                                }
                            >
                                {CHANNEL_LABELS[ch] || ch}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Input and buttons container - inline-block to prevent stretching */}
            <div className="inline-block space-y-2">
                {/* Top row: OTP input boxes */}
                <div>
                    <OtpInput value={otpValue} onChange={onOtpChange} otpLength={otpLength} isValid={isValid} />
                </div>

                {/* Bottom row: Request button + Validate button side by side */}
                <div className="flex gap-2 w-full">
                    <RequestButton
                        hasRequested={hasRequested}
                        cooldown={cooldown}
                        onClick={hasRequested ? resendOtp : requestOtp}
                        disabled={!isProceedReadyOtp || cooldown > 0}
                        isValid={isValid}
                        otpReady={hasRequested && otpValue && otpValue.length === otpLength}
                    />
                    <div className="flex-1">
                        <ValidateButton
                            onClick={validateOtp}
                            disabled={!isProceedReadyOtp || !hasRequested || !otpValue || otpValue.length !== otpLength}
                            isReady={hasRequested && otpValue && otpValue.length === otpLength}
                            isValid={isValid}
                        />
                    </div>
                </div>
            </div>

            {/* Validation feedback - shows error or success */}
            {(error || isValid) && (
                <p className={`text-sm mt-2 ${isValid ? 'text-emerald-600 py-1' : 'text-red-600 py-1'}`}>
                    {isValid ? 'Cell number verified' : error || `Please enter ${otpLength} characters`}
                </p>
            )}
        </div>
    );
}
